import { type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import AuthBrandMark from './auth-brand-mark';
import StepProgress from './step-progress';
import { useLoginFlowVariant } from './variant';

interface AuthStepShellProps {
    heading: string;
    description?: ReactNode;
    onBack?: () => void;
    /** 1-based progress index; omitted progress is hidden. */
    step?: number;
    showLogo?: boolean;
    headingAlign?: 'center' | 'start';
    children: ReactNode;
}

export default function AuthStepShell({
    heading,
    description,
    onBack,
    step,
    showLogo = true,
    headingAlign = 'center',
    children,
}: AuthStepShellProps): ReactElement {
    const { t } = useTranslation('login');
    const variant = useLoginFlowVariant();
    const isPage = variant === 'page';
    const headingRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        if (!isPage) return;
        headingRef.current?.focus();
    }, [isPage, heading]);

    return (
        <div className={isPage ? 'space-y-0' : 'h-full'}>
            {isPage ? (
                onBack ? (
                    <button
                        type="button"
                        className="mb-4 -ml-2 inline-flex cursor-pointer items-center gap-1 px-2 text-sm text-muted-foreground hover:text-foreground"
                        onClick={onBack}>
                        <ArrowLeft className="size-4" />
                        {t('sheet.backToPreviousStep', { defaultValue: 'Back' })}
                    </button>
                ) : null
            ) : (
                <div
                    className={cn(
                        'min-h-[65px] border-b border-login-sheet-divider py-4 pr-4 pl-6',
                        onBack && 'flex items-center gap-2'
                    )}>
                    {onBack ? (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 cursor-pointer"
                            onClick={onBack}
                            aria-label={t('sheet.back', { defaultValue: 'Back' })}>
                            <ArrowLeft className="size-5" />
                        </Button>
                    ) : null}
                    <h2 className={cn('text-[20px] font-medium', showLogo && 'hidden')}>{heading}</h2>
                </div>
            )}

            <div className={isPage ? 'space-y-6 p-0' : 'p-6'}>
                {isPage && step ? <StepProgress current={step} /> : null}
                {showLogo ? <AuthBrandMark alt={heading} /> : null}
                {isPage || showLogo ? (
                    isPage ? (
                        <h1
                            ref={headingRef}
                            tabIndex={-1}
                            className={cn(
                                'mb-2 text-2xl font-medium tracking-tight outline-none sm:text-[28px]',
                                headingAlign === 'center' ? 'text-center' : 'text-left'
                            )}>
                            {heading}
                        </h1>
                    ) : (
                        <h2
                            className={cn(
                                'mb-2 text-[20px] font-medium',
                                headingAlign === 'center' ? 'text-center' : 'text-left'
                            )}>
                            {heading}
                        </h2>
                    )
                ) : null}
                {description ? (
                    <div className="mb-6 text-center text-sm text-muted-foreground">{description}</div>
                ) : null}
                {children}
            </div>
        </div>
    );
}
